import React, { useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

interface DailyPerformanceData {
  date: string;
  dc_capacity: number;
  ac_capacity: number;
  cuf: number;
  pr: number;
  energy_kwh: number;
  poa_irradiance: number;
  poa_energy: number;
  grid_availability: number;
  plant_availability: number;
}

interface PerformanceTableProps {
  data: DailyPerformanceData[];
  loading?: boolean;
}

const PerformanceTable: React.FC<PerformanceTableProps> = ({ data, loading }) => {
  const [sortField, setSortField] = useState<keyof DailyPerformanceData>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: keyof DailyPerformanceData) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    const modifier = sortDirection === 'asc' ? 1 : -1;
    
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal) * modifier;
    }
    return ((aVal as number) - (bVal as number)) * modifier;
  });

  const exportToCSV = () => {
    const headers = ['Date', 'DC Capacity', 'AC Capacity', 'CUF (%)', 'PR (%)', 'Energy (kWh)', 'POA Irradiance', 'POA Energy', 'Grid Availability (%)', 'Plant Availability (%)'];
    const csvData = sortedData.map(row => [
      row.date,
      row.dc_capacity,
      row.ac_capacity,
      row.cuf,
      row.pr,
      row.energy_kwh,
      row.poa_irradiance,
      row.poa_energy,
      row.grid_availability,
      row.plant_availability
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plant_performance.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const SortIcon = ({ field }: { field: keyof DailyPerformanceData }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUpIcon className="w-4 h-4" /> : 
      <ChevronDownIcon className="w-4 h-4" />;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Plant Performance Table
        </h3>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              {[
                { key: 'date', label: 'Date' },
                { key: 'dc_capacity', label: 'DC Capacity' },
                { key: 'ac_capacity', label: 'AC Capacity' },
                { key: 'cuf', label: 'CUF (%)' },
                { key: 'pr', label: 'PR (%)' },
                { key: 'energy_kwh', label: 'Energy (kWh)' },
                { key: 'poa_irradiance', label: 'POA Irradiance' },
                { key: 'poa_energy', label: 'POA Energy' },
                { key: 'grid_availability', label: 'Grid Avail. (%)' },
                { key: 'plant_availability', label: 'Plant Avail. (%)' }
              ].map(col => (
                <th
                  key={col.key}
                  className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                  onClick={() => handleSort(col.key as keyof DailyPerformanceData)}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    <SortIcon field={col.key as keyof DailyPerformanceData} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, index) => (
              <tr key={index} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="py-3 px-4 text-gray-900 dark:text-white">{row.date}</td>
                <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{row.dc_capacity.toFixed(2)}</td>
                <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{row.ac_capacity.toFixed(2)}</td>
                <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{row.cuf.toFixed(1)}%</td>
                <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{row.pr.toFixed(1)}%</td>
                <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{row.energy_kwh.toLocaleString()}</td>
                <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{row.poa_irradiance.toFixed(2)}</td>
                <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{row.poa_energy.toFixed(2)}</td>
                <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{row.grid_availability.toFixed(1)}%</td>
                <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{row.plant_availability.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PerformanceTable;